import { apiFetch } from "./client";

export type TmTaskStatus = "TODO" | "IN_PROGRESS" | "IN_REVIEW" | "DONE" | "BLOCKED" | "CANCELLED";
export type TmTaskPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

export type TmTagRef = {
  id: string;
  name: string;
  color: string | null;
};

export type TmTaskView = {
  id: string;
  title: string;
  description: string | null;
  status: TmTaskStatus;
  priority: TmTaskPriority;
  blockedReason: string | null;
  assigneeUserId: string;
  assigneeName: string;
  validatorUserId: string | null;
  validatorName: string | null;
  requesterUserId: string | null;
  createdByUserId: string;
  createdByName: string;
  dueDate: string | null;
  startedAt: string | null;
  completedAt: string | null;
  projectId: string | null;
  projectName: string | null;
  meetingId: string | null;
  meetingTitle: string | null;
  parentTaskId: string | null;
  parentTaskTitle: string | null;
  estimatedHours: string | null;
  tags: TmTagRef[];
  openPredecessorsCount: number;
  createdAt: string;
  updatedAt: string;
};

export type TmTaskCreate = {
  title: string;
  description?: string | null;
  priority?: TmTaskPriority;
  assigneeUserId: string;
  validatorUserId?: string | null;
  requesterUserId?: string | null;
  dueDate?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  parentTaskId?: string | null;
  estimatedHours?: string | number | null;
};

export type TmTaskUpdate = {
  title?: string;
  description?: string;
  priority?: TmTaskPriority;
  validatorUserId?: string | null;
  dueDate?: string | null;
  estimatedHours?: string | number | null;
};

export type TmTagView = {
  id: string;
  name: string;
  color: string | null;
  createdAt: string;
};

export type TmTaskWatcherView = {
  userId: string;
  name: string;
  email: string;
};

export type TmDependencyEdge = {
  id: string;
  otherTaskId: string;
  otherTaskTitle: string;
  otherTaskStatus: TmTaskStatus;
};

export type TmTaskTransition = {
  toStatus: TmTaskStatus;
  comment?: string;
  blockedReason?: string;
};

export type TmUserOption = { id: string; name: string; email: string };

export type TmAttachmentView = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: string;
  uploadedByName: string;
  uploadedAt: string;
};

export type TmPresignResponse = { objectKey: string; url: string };

export type TmProjectStatus = "PLANNING" | "ACTIVE" | "ON_HOLD" | "COMPLETED" | "CANCELLED";
export type TmProjectVisibility = "TEAM_ONLY" | "MEMBERS_ONLY" | "COMPANY_WIDE";
export type TmProjectMemberRole = "OWNER" | "CONTRIBUTOR" | "VIEWER";

export type TmProjectView = {
  id: string;
  name: string;
  description: string | null;
  status: TmProjectStatus;
  visibility: TmProjectVisibility;
  startDate: string | null;
  targetEndDate: string | null;
  ownerUserId: string;
  ownerName: string;
  teamId: string | null;
  teamName: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TmProjectMemberView = {
  userId: string;
  name: string;
  email: string;
  role: TmProjectMemberRole;
};

export type TmProjectCreate = {
  name: string;
  description?: string | null;
  status?: TmProjectStatus;
  startDate?: string | null;
  targetEndDate?: string | null;
  teamId?: string | null;
};

export type TmProjectUpdate = {
  name?: string;
  description?: string | null;
  status?: TmProjectStatus;
  startDate?: string | null;
  targetEndDate?: string | null;
};

export type TmTeamMemberRole = "LEAD" | "MEMBER";
export type TmTeamView = {
  id: string;
  name: string;
  description: string | null;
  parentTeamId: string | null;
  parentTeamName: string | null;
  leadUserId: string | null;
  leadName: string | null;
};
export type TmTeamMemberView = {
  userId: string;
  name: string;
  email: string;
  role: TmTeamMemberRole;
};

export type TmMeetingView = {
  id: string;
  title: string;
  agenda: string | null;
  notes: string | null;
  scheduledAt: string;
  durationMinutes: number;
  location: string | null;
  projectId: string | null;
  projectName: string | null;
  scribeUserId: string | null;
  scribeName: string | null;
  createdByUserId: string;
  createdByName: string;
  recurrenceRule: string | null;
  recurrenceParentId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TmMeetingCreate = {
  title: string;
  agenda?: string | null;
  scheduledAt: string;
  durationMinutes?: number;
  location?: string | null;
  projectId?: string | null;
  scribeUserId?: string | null;
  recurrenceRule?: string | null;
};

export type TmMeetingUpdate = {
  title?: string;
  agenda?: string;
  notes?: string;
  location?: string;
};

export type TmCommentView = {
  id: string;
  content: string;
  authorUserId: string;
  authorName: string;
  authorEmail: string;
  parentCommentId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TmActivityEntityType = "TASK" | "MEETING" | "PROJECT" | "COMMENT";

export type TmActivityView = {
  id: string;
  entityType: TmActivityEntityType;
  entityId: string;
  action: string;
  actorId: string | null;
  actorName: string | null;
  changes: Record<string, unknown> | null;
  createdAt: string;
};

export type TmActivityPage = {
  content: TmActivityView[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export const tmApi = {
  users:          () => apiFetch<TmUserOption[]>("/tm/users"),
  mine:           () => apiFetch<TmTaskView[]>("/tm/tasks/mine"),
  assignedByMe:   () => apiFetch<TmTaskView[]>("/tm/tasks/assigned-by-me"),
  forValidation:  () => apiFetch<TmTaskView[]>("/tm/tasks/for-validation"),
  byProject:      (projectId: string) => apiFetch<TmTaskView[]>(`/tm/tasks/by-project/${projectId}`),
  subtasks:       (parentId: string) => apiFetch<TmTaskView[]>(`/tm/tasks/${parentId}/subtasks`),

  // Tags
  listTags:       () => apiFetch<TmTagView[]>("/tm/tags"),
  createTag:      (body: { name: string; color?: string | null }) =>
                    apiFetch<TmTagView>("/tm/tags", { method: "POST", body }),
  attachTag:      (taskId: string, tagId: string) =>
                    apiFetch<TmTaskView>(`/tm/tasks/${taskId}/tags/${tagId}`, { method: "POST" }),
  detachTag:      (taskId: string, tagId: string) =>
                    apiFetch<TmTaskView>(`/tm/tasks/${taskId}/tags/${tagId}`, { method: "DELETE" }),

  // Watchers
  listWatchers:   (taskId: string) => apiFetch<TmTaskWatcherView[]>(`/tm/tasks/${taskId}/watchers`),
  watch:          (taskId: string) => apiFetch<void>(`/tm/tasks/${taskId}/watch`, { method: "POST" }),
  unwatch:        (taskId: string) => apiFetch<void>(`/tm/tasks/${taskId}/watch`, { method: "DELETE" }),

  // Dependencies
  blockedBy:      (taskId: string) => apiFetch<TmDependencyEdge[]>(`/tm/tasks/${taskId}/dependencies/blocked-by`),
  blocks:         (taskId: string) => apiFetch<TmDependencyEdge[]>(`/tm/tasks/${taskId}/dependencies/blocks`),
  addBlockedBy:   (taskId: string, predecessorTaskId: string) =>
                    apiFetch<TmDependencyEdge>(`/tm/tasks/${taskId}/dependencies`,
                      { method: "POST", body: { predecessorTaskId } }),
  removeDependency: (taskId: string, dependencyId: string) =>
                    apiFetch<void>(`/tm/tasks/${taskId}/dependencies/${dependencyId}`, { method: "DELETE" }),
  get:            (id: string) => apiFetch<TmTaskView>(`/tm/tasks/${id}`),
  create:         (body: TmTaskCreate) => apiFetch<TmTaskView>("/tm/tasks", { method: "POST", body }),
  update:         (id: string, body: TmTaskUpdate) =>
                    apiFetch<TmTaskView>(`/tm/tasks/${id}`, { method: "PATCH", body }),
  transition:     (id: string, body: TmTaskTransition) =>
                    apiFetch<TmTaskView>(`/tm/tasks/${id}/transition`, { method: "POST", body }),
  reassign:       (id: string, newAssigneeUserId: string, reason?: string) =>
                    apiFetch<TmTaskView>(`/tm/tasks/${id}/reassign`,
                      { method: "POST", body: { newAssigneeUserId, reason } }),

  listAttachments:    (taskId: string) =>
                        apiFetch<TmAttachmentView[]>(`/tm/tasks/${taskId}/attachments`),
  presignAttachment:  (taskId: string, body: { fileName: string; contentType: string; sizeBytes: number }) =>
                        apiFetch<TmPresignResponse>(`/tm/tasks/${taskId}/attachments/presign`,
                          { method: "POST", body }),
  confirmAttachment:  (taskId: string, body: { objectKey: string; fileName: string; contentType: string; sizeBytes: number }) =>
                        apiFetch<TmAttachmentView>(`/tm/tasks/${taskId}/attachments/confirm`,
                          { method: "POST", body }),
  attachmentDownloadUrl: (taskId: string, attachmentId: string) =>
                        apiFetch<{ url: string }>(`/tm/tasks/${taskId}/attachments/${attachmentId}/download-url`),

  listComments:    (taskId: string) =>
                     apiFetch<TmCommentView[]>(`/tm/tasks/${taskId}/comments`),
  addComment:      (taskId: string, content: string, parentCommentId?: string | null) =>
                     apiFetch<TmCommentView>(`/tm/tasks/${taskId}/comments`,
                       { method: "POST", body: { content, parentCommentId: parentCommentId ?? null } }),

  listProjects:    () => apiFetch<TmProjectView[]>("/tm/projects"),
  getProject:      (id: string) => apiFetch<TmProjectView>(`/tm/projects/${id}`),
  createProject:   (body: TmProjectCreate) =>
                     apiFetch<TmProjectView>("/tm/projects", { method: "POST", body }),
  updateProject:   (id: string, body: TmProjectUpdate) =>
                     apiFetch<TmProjectView>(`/tm/projects/${id}`, { method: "PATCH", body }),
  listProjectMembers: (id: string) =>
                     apiFetch<TmProjectMemberView[]>(`/tm/projects/${id}/members`),
  addProjectMember: (id: string, userId: string, role: TmProjectMemberRole) =>
                     apiFetch<TmProjectMemberView>(`/tm/projects/${id}/members`,
                       { method: "POST", body: { userId, role } }),

  listMeetings:    () => apiFetch<TmMeetingView[]>("/tm/meetings"),
  getMeeting:      (id: string) => apiFetch<TmMeetingView>(`/tm/meetings/${id}`),
  createMeeting:   (body: TmMeetingCreate) =>
                     apiFetch<TmMeetingView>("/tm/meetings", { method: "POST", body }),
  updateMeeting:   (id: string, body: TmMeetingUpdate) =>
                     apiFetch<TmMeetingView>(`/tm/meetings/${id}`, { method: "PATCH", body }),

  listActivity: (params: { entityType?: TmActivityEntityType; actorId?: string; page?: number; size?: number } = {}) => {
    const usp = new URLSearchParams();
    if (params.entityType) usp.set("entityType", params.entityType);
    if (params.actorId)    usp.set("actorId", params.actorId);
    if (params.page != null) usp.set("page", String(params.page));
    if (params.size != null) usp.set("size", String(params.size));
    const qs = usp.toString();
    return apiFetch<TmActivityPage>(`/tm/activity${qs ? `?${qs}` : ""}`);
  },

  listTeams:       () => apiFetch<TmTeamView[]>("/tm/teams"),
  getTeam:         (id: string) => apiFetch<TmTeamView>(`/tm/teams/${id}`),
  listTeamMembers: (id: string) => apiFetch<TmTeamMemberView[]>(`/tm/teams/${id}/members`),
  createTeam:      (body: { name: string; description?: string }) =>
                     apiFetch<TmTeamView>("/tm/teams", { method: "POST", body }),
  addTeamMember:   (id: string, userId: string, role: TmTeamMemberRole) =>
                     apiFetch<TmTeamMemberView>(`/tm/teams/${id}/members`,
                       { method: "POST", body: { userId, role } }),
};
